import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCartQuery } from '../../cart/cart.services';
import { Heart, ShoppingBag, Plus, Minus, Star, ShieldCheck, Clock } from 'lucide-react';
import { Paragraph, Caption, Badge, AppImage, Button } from '@shopmuchmore/design-system';
import type { Product } from '../../home/home.types';
import { getProductUrl, calculateDiscount } from '../../home/home.types';
import { cn } from "@shopmuchmore/design-system";
import { formatPrice } from "@shopmuchmore/common-utils";
import { useToggleWishlistMutation, useIsWishlisted } from '../../wishlist/wishlist.services';
import { useAuthStore } from '../../auth/auth.store';
import { toast } from 'sonner';

// Helper to calculate time remaining
function getTimeRemaining(endTime: string): { hours: number; minutes: number; seconds: number; isExpired: boolean; isWithin24Hours: boolean } {
  const now = new Date().getTime();
  const end = new Date(endTime).getTime();
  const diff = end - now;

  if (diff <= 0) {
    return { hours: 0, minutes: 0, seconds: 0, isExpired: true, isWithin24Hours: false };
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  const isWithin24Hours = hours < 24;

  return { hours, minutes, seconds, isExpired: false, isWithin24Hours };
}

// Format countdown display
function formatCountdown(hours: number, minutes: number, seconds: number): string {
  const h = hours.toString().padStart(2, '0');
  const m = minutes.toString().padStart(2, '0');
  const s = seconds.toString().padStart(2, '0');
  return `${h}:${m}:${s}`;
}

interface ProductCardProps {
  product: Product;
  widgetType: string;
  isWishlisted?: boolean;
  onQuantityChange?: (productId: string, quantity: number) => void;
  onWishlistToggle?: (productId: string) => void;
}

function ProductCard({
  product,
  widgetType,
  isWishlisted = false,
  onQuantityChange,
  onWishlistToggle,
}: ProductCardProps) {
  const { data: cart } = useCartQuery();
  const quantity = cart?.items.find(i => i.product.product_slug === product.slug)?.quantity ?? 0;
  const navigate = useNavigate();
  const { customer } = useAuthStore();
  
  const isItemWishlisted = useIsWishlisted(product.slug);
  const { mutate: toggleWishlist } = useToggleWishlistMutation();
  
  const currentIsWishlisted = isWishlisted || isItemWishlisted;
  const [countdown, setCountdown] = useState<{ hours: number; minutes: number; seconds: number; isWithin24Hours: boolean } | null>(null);
  const isOutOfStock = product.stockCount === 0;


  const discount = product.discountPercentage || calculateDiscount(product.currentPrice, product.originalPrice);
  const productUrl = getProductUrl(product, widgetType);

  // Countdown timer effect
  useEffect(() => {
    if (!product.discountEndsAt) return;

    const updateCountdown = () => {
      const timeLeft = getTimeRemaining(product.discountEndsAt!);
      if (timeLeft.isExpired) {
        setCountdown(null);
      } else if (timeLeft.isWithin24Hours) {
        setCountdown(timeLeft);
      } else {
        setCountdown(null);
      }
    };

    // Initial update
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [product.discountEndsAt]);

  const handleCardClick = () => {
    console.log(`[Product Click] ${product.name}`);
    console.log(`[Product Click] URL: ${productUrl}`);
    // if (isOutOfStock) return; // User wants to allow viewing details
    navigate(productUrl);
  };

  const handleWishlistClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!customer) {
      toast.error('Please login to save items');
      return;
    }
    
    if (onWishlistToggle) {
      onWishlistToggle(product.id);
    } else {
      toggleWishlist({ slug: product.slug, isSaved: currentIsWishlisted });
    }
  };

  const handleQuickAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    const stock = product.stockCount;
    if (stock && stock > 0 && quantity < stock) {
      onQuantityChange?.(product.slug, 1);
    }
  };

  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (product.stockCount && quantity < product.stockCount) {
      onQuantityChange?.(product.slug, quantity + 1);
    } else {
      console.log(`[ProductCard] Cannot exceed stock: ${product.stockCount}`);
    }
  };

  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    onQuantityChange?.(product.slug, Math.max(0, quantity - 1));
  };

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    return (
      <div className="flex items-center gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            className={`size-3.5 ${i < fullStars
              ? 'fill-amber-400 text-amber-400'
              : i === fullStars && hasHalfStar
                ? 'fill-amber-400/50 text-amber-400'
                : 'fill-gray-200 text-gray-200'
              }`}
          />
        ))}
      </div>
    );
  };



  return (
    <div
      onClick={handleCardClick}
      className="relative p-3 w-[98%] max-w-[250px] mx-auto bg-white rounded-2xl overflow-hidden cursor-pointer group/card border-[1.5px] border-gray-100 transition-all duration-300 hover:-translate-y-1 h-full flex flex-col"
    >
      {/* Image Container */}
      <div className="relative aspect-[4/3] overflow-hidden flex-shrink-0 bg-transparent">
        <AppImage
          src={product.image || undefined}
          alt={product.name}
          isOutOfStock={isOutOfStock}
        />

        {/* Out of Stock Overlay */}
        {isOutOfStock && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50/10">
            <div className="bg-gray-900/80 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-bold border border-white/10">
              Out of Stock
            </div>
          </div>
        )}

        {/* Badges Container - Stacked vertically (max 3) */}
        {(() => {
          const badges: React.ReactNode[] = [];

          // Priority 1: Sale Badge
          if (discount && discount > 0) {
            badges.push(
              <Badge key="discount" variant="destructive" size="sm">
                -{Number(discount).toString()}%
              </Badge>
            );
          }


          // Priority 2: Badge Text (Best Seller, Eco-Friendly, etc.)
          if (product.badgeText) {
            badges.push(
              <Badge key="badge" variant="default" size="sm">
                {product.badgeText}
              </Badge>
            );
          }

          // Priority 3: Age Restriction Badge
          if (product.ageRestricted) {
            badges.push(
              <Badge key="age" className="bg-gray-900 text-white hover:bg-gray-800" size="sm" leadingIcon={<ShieldCheck className="size-3" />}>
                18+
              </Badge>
            );
          }

          // Priority 4: Low Stock Badge (1–10 remaining, not out of stock)
          if (
            !isOutOfStock &&
            product.stockCount !== undefined &&
            product.stockCount > 0 &&
            product.stockCount <= 10
          ) {
            badges.push(
              <Badge key="stock" variant="warning" size="sm">
                Only {product.stockCount} left
              </Badge>
            );
          }

          if (badges.length === 0) return null;

          return (
            <div className="absolute top-3 left-3 flex flex-col gap-1.5">
              {badges.slice(0, 3)}
            </div>
          );
        })()}

        {/* Wishlist Button */}
        <Button
          size="icon-sm"
          variant="ghost"
          onClick={handleWishlistClick}
          aria-label={currentIsWishlisted ? "Remove from wishlist" : "Add to wishlist"}
          className={`absolute top-3 right-3 w-8 h-8 rounded-full transition-all duration-200 ${currentIsWishlisted
            ? 'bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-600 shadow-none'
            : 'bg-white/80 text-gray-400 hover:bg-white hover:text-red-500 shadow-none'
            }`}
        >
          <Heart className={`size-4 ${currentIsWishlisted ? 'fill-current' : ''}`} />
        </Button>

        {/* Smart Cart Controller removed from here */}
      </div>

      {/* Content */}
      <div className="pt-3 flex flex-col flex-1">
        {/* Sold Count */}
        {product.soldCount ? (<Caption className="text-xs mb-1.5">
            {product.soldCount}+ bought
          </Caption>) : null}

        {/* Rating */}
        {product.reviews && product.reviews > 0 ? (
          <div className="flex items-center gap-2 mb-2">
            {renderStars(product.rating)}
            <Caption className="text-xs">
              ({product.reviews.toLocaleString()})
            </Caption>
          </div>
        ) : null}

        {/* Merchant Name */}
        {product.merchantName && (
          <Caption className="text-xs text-gray-500 mb-1">
            {product.merchantName}
          </Caption>
        )}

        {/* Product Name */}
        <Paragraph
          size="sm"
          weight="medium"
          leading="snug"
          className="line-clamp-2 mb-2 group-hover/card:text-primary transition-colors capitalize"
        >
          {product.name}
        </Paragraph>

        <div className="mt-auto flex items-center justify-between gap-2 flex-wrap">
          <div>
            {/* Price */}
            <div className="flex flex-col items-start justify-center">
              <span className="text-lg font-bold text-gray-900 leading-none">
                {formatPrice(product.currentPrice)}
              </span>
              {product.originalPrice && (
                <span className="text-[11px] text-gray-500 line-through mt-1">
                  {formatPrice(product.originalPrice)}
                </span>
              )}
            </div>
          </div>

          {/* Smart Cart Controller */}
          <div
            className={cn(
              "transition-all duration-300 z-20 flex-shrink-0",
              isOutOfStock && "opacity-50 pointer-events-none"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {quantity === 0 ? (
              <Button
                size="icon-sm"
                onClick={handleQuickAdd}
                aria-label="Add to cart"
                className="w-8 h-8 rounded-full bg-primary text-white hover:bg-primary hover:scale-110 transition-all duration-200 cursor-pointer shadow-none"
              >
                <ShoppingBag className="size-4" />
              </Button>
            ) : (
              <div className="flex items-center gap-1 bg-gray-50 rounded-full border border-gray-200 p-0.5">
                <Button
                  size="icon-xs"
                  variant="ghost"
                  onClick={handleDecrement}
                  aria-label="Decrease quantity"
                  className="w-6 h-6 rounded-full bg-white text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer shadow-sm"
                >
                  <Minus className="size-3" />
                </Button>
                <span className="w-5 text-center font-semibold text-[11px]">{quantity}</span>
                <Button
                  size="icon-xs"
                  onClick={handleIncrement}
                  aria-label="Increase quantity"
                  disabled={product.stockCount !== undefined && quantity >= product.stockCount}
                  className={cn(
                    "w-6 h-6 rounded-full transition-all cursor-pointer shadow-sm",
                    product.stockCount !== undefined && quantity >= product.stockCount
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-primary text-white hover:bg-primary/90"
                  )}
                >
                  <Plus className="size-3" />
                </Button>
              </div>
            )}
          </div>
        </div>

          {/* Countdown Timer - Shows when discount ends in less than 24 hours */}
          {countdown && (
            <div className="mt-2 inline-flex items-center gap-1.5 bg-red-600 text-white text-xs font-bold px-2.5 py-1.5 rounded">
              <Clock className="size-3.5" />
              <span>Ends in {formatCountdown(countdown.hours, countdown.minutes, countdown.seconds)}</span>
            </div>
          )}
      </div>
    </div>
  );
}

export default ProductCard;
